import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ScanLine, ArrowRight, ArrowUpRight, MoveHorizontal } from "lucide-react";
import api from "../lib/api";
import { Reveal, Overline } from "../components/Reveal";
import { CrownMark } from "../components/CrownMark";
import { TAGLINE } from "../data/contact";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import CourtConfigurator from "../components/CourtConfigurator";
import { PROCESS, SERVICES, IMAGES, TESTIMONIALS } from "../data/content";
import Seo from "../components/Seo";
import { responsive } from "../lib/img";
import TransformationReel, { ReelCta } from "../components/TransformationReel";
import SpeedyMethod from "../components/SpeedyMethod";
import WhyUs from "../components/WhyUs";
import TrustedBy from "../components/TrustedBy";
import ComplianceBand from "../components/ComplianceBand";
import ContactSection from "../components/ContactSection";
import BlogTeaser from "../components/BlogTeaser";

const ProcessRow = ({ step, i }) => (
  <Reveal delay={i * 0.05}>
    <div className="group grid grid-cols-[auto_1fr] md:grid-cols-[120px_1fr_auto] items-center gap-6 border-t border-white/10 py-8 hover:bg-white/[0.02] transition-colors px-2">
      <span className="font-heading font-black text-4xl md:text-6xl text-zinc-800 group-hover:text-neon transition-colors">{step.n}</span>
      <div>
        <h3 className="font-heading font-bold text-xl md:text-2xl">{step.title}</h3>
        <p className="text-sm text-zinc-300 mt-1 max-w-md">{step.text}</p>
      </div>
      <ArrowUpRight className="hidden md:block text-zinc-700 group-hover:text-neon transition-colors" size={28} />
    </div>
  </Reveal>
);

export default function Home() {
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 160]);
  const heroScale = useTransform(scrollY, [0, 600], [1, 1.15]);

  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}); }, []);

  const feat = projects[0];

  const goQuote = (config) => nav("/quote", { state: { config } });

  return (
    <div>
      <Seo title="Court Resurfacing Sydney | Speedy Resurfacing — Basketball, Tennis & Netball Courts"
           description="Sydney's specialist court resurfacing crew. Basketball, tennis, netball & multi-sport court resurfacing, custom design and precision line marking across NSW. Free quotes."
           path="/" />
      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center overflow-hidden noise" data-testid="hero">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <img {...responsive(IMAGES.heroCourt, "100vw", [640, 1000, 1200, 1600])} alt="Backyard Jordan court by Speedy Resurfacing"
               fetchpriority="high" decoding="async"
               className="h-full w-full object-cover"
               style={{ filter: "contrast(1.1) saturate(1.2) brightness(1.05)" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/25 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian/80 via-obsidian/20 to-transparent" />
        </motion.div>

        <div className="relative z-10 mx-auto max-w-7xl w-full px-5 md:px-10 pt-28">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
            <Overline>Court Resurfacing · Restoration · Custom Design</Overline>
            <h1 className="mt-6 font-heading font-black text-6xl sm:text-7xl lg:text-8xl xl:text-9xl leading-[0.85] tracking-tighter">
              <span className="lux-white">YOUR COURT.</span><br /><span className="lux-gold">REBUILT.</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg text-zinc-100" style={{ textShadow: "0 1px 14px rgba(0,0,0,0.7)" }}>
              Professional court resurfacing, restoration, custom design and precision line marking — powered by the experience behind Speedy Line Marking.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4">
              <button onClick={() => nav("/customiser")} data-testid="hero-design-btn"
                className="group inline-flex items-center justify-center gap-2 rounded-sm bg-neon px-7 py-4 font-bold text-obsidian hover:bg-neon-hover transition-colors shadow-neon">
                Design My Court <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={() => nav("/quote")} data-testid="hero-quote-btn"
                className="rounded-sm border border-white/25 px-7 py-4 font-bold hover:border-neon hover:text-neon transition-colors">
                Request a Quote
              </button>
              <Link to="/gallery" data-testid="hero-explore-btn"
                className="inline-flex items-center justify-center sm:justify-start gap-2 px-3 py-3 sm:py-4 font-bold text-zinc-300 hover:text-neon transition-colors">
                Explore Our Work <ArrowUpRight size={18} />
              </Link>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-8 inset-x-0 z-10 mx-auto max-w-7xl px-5 md:px-10 hidden md:flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Scroll to explore</span>
          <span className="inline-flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-gold"><CrownMark className="h-4" animate={false} /> {TAGLINE}</span>
        </div>
      </section>

      {/* BRAND ARCHITECTURE */}
      <section className="mx-auto max-w-7xl px-5 md:px-10 py-24" data-testid="brand-architecture">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <Overline className="text-gold">From the Speedy family</Overline>
            <h2 className="mt-5 font-heading font-black text-3xl md:text-5xl leading-[0.95] tracking-tight">
              A NEW BRANCH OF AN ESTABLISHED TREE.
            </h2>
            <p className="mt-6 text-zinc-300 max-w-lg">
              Think of <span className="text-white font-semibold">Speedy Line Marking</span> as the established main tree.
              <span className="text-neon font-semibold"> Speedy Resurfacing</span> is a specialised court resurfacing division built from the experience of Speedy Line Marking.
            </p>
            <a href="https://www.speedylinemarking.com.au" target="_blank" rel="noreferrer"
               className="mt-6 inline-flex items-center gap-1 py-2 text-gold hover:text-gold-hover font-medium">
              Explore the parent brand <ArrowUpRight size={16} />
            </a>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative rounded-lg border border-gold/20 bg-obsidian-800 p-8 shadow-gold">
              <div className="flex flex-col items-center text-center gap-3">
                <span className="font-heading font-black text-2xl text-white">SPEEDY LINE MARKING</span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Established Parent Brand</span>
                <div className="h-12 w-px bg-gradient-to-b from-white/40 to-gold my-2" />
                <img src="/brand/speedy-resurfacing-logo-960.webp" srcSet="/brand/speedy-resurfacing-logo-960.webp 960w, /brand/speedy-resurfacing-logo-1893.webp 1893w" sizes="260px"
                     alt="Speedy Resurfacing" decoding="async" className="logo-glow h-20 sm:h-24 w-auto" data-testid="brand-architecture-logo" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-gold">Specialist Court Resurfacing Division</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* THE SPEEDY METHOD */}
      <SpeedyMethod />

      {/* BEFORE / AFTER */}
      {feat && (
        <section className="mx-auto max-w-7xl px-5 md:px-10 py-16" data-testid="home-before-after">
          <Reveal>
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
              <div>
                <Overline>The Transformation Engine</Overline>
                <h2 className="mt-4 font-heading font-black text-3xl md:text-5xl tracking-tight">DRAG TO SEE THE DIFFERENCE.</h2>
              </div>
              <span className="hidden md:inline-flex items-center gap-2 font-mono text-xs text-zinc-500"><MoveHorizontal size={14} /> Drag the handle</span>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <BeforeAfterSlider before={feat.before_image || IMAGES.worn} after={feat.after_image || IMAGES.speedyBball} testid="home-slider" />
            <div className="mt-5 flex flex-wrap gap-x-8 gap-y-2 font-mono text-xs text-zinc-500">
              {feat.location && <span>LOCATION · <span className="text-zinc-300">{feat.location}</span></span>}
              {feat.court_type && <span>TYPE · <span className="text-zinc-300">{feat.court_type}</span></span>}
              {feat.surface && <span>SURFACE · <span className="text-zinc-300">{feat.surface}</span></span>}
            </div>
          </Reveal>
        </section>
      )}

      {/* WATCH THE TRANSFORMATION */}
      <section className="mx-auto max-w-7xl px-5 md:px-10 py-24" data-testid="home-transformation-reel">
        <Reveal>
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <Overline>Real Projects · Real Proof</Overline>
              <h2 className="mt-4 font-heading font-black text-3xl md:text-5xl tracking-tight">WATCH THE <span className="text-neon">TRANSFORMATION.</span></h2>
            </div>
            <ReelCta />
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <TransformationReel />
        </Reveal>
      </section>

      {/* COURT SCAN CTA */}
      <section className="mx-auto max-w-7xl px-5 md:px-10 py-24" data-testid="home-scan-cta">
        <Reveal>
          <div className="relative overflow-hidden rounded-lg border border-neon/20 bg-obsidian-800 p-10 md:p-16 grid-lines">
            <div className="relative z-10 max-w-2xl">
              <Overline>Signature Feature</Overline>
              <h2 className="mt-5 font-heading font-black text-4xl md:text-6xl tracking-tight">
                SPEEDY COURT SCAN<span className="text-neon align-super text-2xl">™</span>
              </h2>
              <p className="mt-5 text-zinc-300">
                Upload photos of your existing court and let our intelligent configurator map out colours, layouts and line marking —
                with a final assessment completed by the Speedy team.
              </p>
              <button onClick={() => nav("/court-scan")} data-testid="home-scan-btn"
                className="mt-8 inline-flex items-center gap-2 rounded-sm bg-neon px-7 py-4 font-bold text-obsidian hover:bg-neon-hover transition-colors shadow-neon">
                <ScanLine size={18} /> Scan My Court
              </button>
            </div>
            <ScanLine className="absolute -right-8 -bottom-8 text-neon/5" size={280} />
          </div>
        </Reveal>
      </section>

      {/* PROCESS */}
      <section className="mx-auto max-w-7xl px-5 md:px-10 py-16" data-testid="home-process">
        <Reveal><Overline>The Resurfacing Process</Overline>
          <h2 className="mt-4 font-heading font-black text-3xl md:text-5xl tracking-tight mb-6">SIX STEPS. ONE STANDARD.</h2>
        </Reveal>
        {PROCESS.map((s, i) => <ProcessRow key={s.n} step={s} i={i} />)}
        <div className="border-t border-white/10" />
      </section>

      {/* WHY SPEEDY RESURFACING */}
      <WhyUs />
      <ComplianceBand />

      {/* CONFIGURATOR */}
      <section className="mx-auto max-w-7xl px-5 md:px-10 py-24" data-testid="home-configurator">
        <Reveal>
          <Overline>3D Court Customiser</Overline>
          <h2 className="mt-4 font-heading font-black text-3xl md:text-5xl tracking-tight mb-10">CONFIGURE. VISUALISE. QUOTE.</h2>
        </Reveal>
        <CourtConfigurator onQuote={goQuote} />
      </section>

      {/* SERVICES GRID */}
      <section className="mx-auto max-w-7xl px-5 md:px-10 py-16" data-testid="home-services">
        <Reveal>
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div><Overline>What We Do</Overline>
              <h2 className="mt-4 font-heading font-black text-3xl md:text-5xl tracking-tight">SPECIALIST COURT SERVICES.</h2></div>
            <Link to="/services" className="text-neon hover:text-neon-hover font-medium inline-flex items-center gap-1">All services <ArrowRight size={16} /></Link>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.slice(0, 6).map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.05}>
              <Link to={`/services/${s.slug}`} data-testid={`home-service-${s.slug}`}
                className="group block h-full rounded-lg border border-white/10 bg-obsidian-800 p-7 hover:border-neon/40 transition-colors">
                <div className="flex items-start justify-between">
                  <span className="font-mono text-xs text-zinc-600">{String(i + 1).padStart(2, "0")}</span>
                  <ArrowUpRight className="text-zinc-700 group-hover:text-neon transition-colors" size={20} />
                </div>
                <h3 className="mt-6 font-heading font-bold text-xl group-hover:text-neon transition-colors">{s.title}</h3>
                <p className="mt-2 text-sm text-zinc-300">{s.short}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TRUSTED BY */}
      <TrustedBy />

      {/* REAL REVIEWS */}
      <section className="relative mx-auto max-w-7xl px-5 md:px-10 py-24" data-testid="home-testimonials">
        <Reveal>
          <Overline>Google Reviews · Speedy Line Marking</Overline>
          <h2 className="mt-4 font-heading font-black text-3xl md:text-5xl tracking-tight">TRUSTED. <span className="gold-text">RATED. PROVEN.</span></h2>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.05}>
              <figure className="glass rounded-lg p-6 h-full flex flex-col" data-testid={`testimonial-${i}`}>
                <div className="text-neon tracking-[0.2em] text-sm" aria-label="5 star review">★★★★★</div>
                <blockquote className="mt-4 text-sm text-zinc-300 flex-1">"{t.quote}"</blockquote>
                <figcaption className="mt-4 font-heading font-bold text-sm text-gold">{t.name}</figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
        <p className="mt-5 font-mono text-[10px] text-zinc-600">Real Google reviews of the Speedy Line Marking team — the crew behind Speedy Resurfacing.</p>
      </section>

      {/* FROM THE BLOG */}
      <BlogTeaser />

      {/* TALK TO US */}
      <ContactSection />

      {/* TEAM SPEEDY TEASER */}
      <section className="relative mx-auto max-w-7xl px-5 md:px-10 py-24" data-testid="home-team-teaser">
        <Reveal>
          <div className="relative overflow-hidden rounded-lg border border-gold/20">
            <img {...responsive(IMAGES.greenNetball, "(min-width: 1280px) 1200px, 100vw")} loading="lazy" decoding="async" alt="Team Speedy court resurfacing — multi-court precinct" className="h-[420px] w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/60 to-transparent" />
            <div className="absolute bottom-0 p-8 md:p-12">
              <span className="font-mono text-xs uppercase tracking-[0.3em] gold-text">Speed Force — Resurfacing</span>
              <h2 className="mt-3 font-heading font-black text-3xl md:text-5xl tracking-tight">TEAM SPEEDY. ONE STANDARD.</h2>
              <Link to="/about" className="mt-5 inline-flex items-center gap-2 text-gold hover:text-gold-hover font-bold">
                About Team Speedy <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
